/**
 * Initializes an Alpine.js data object to manage facets/filters on a product listing page (PLP). This component controls the behavior and appearance of facets, including their expansion state, displayed values, and interactions with the user.
 *
 * Key Features:
 * 1. Facet Control: Manages the display and behavior of facets, allowing users to filter products based on various criteria such as category, price range, or product attributes.
 * 2. Display Management: Determines the number of facet values to show by default and provides a mechanism for toggling the display of additional values.
 * 3. Interaction Handling: Listens for user interactions with facets and updates the selected facet values accordingly, ensuring a responsive and intuitive filtering experience.
 * 4. Facet Count Tracking: Tracks the number of selected facet values for each facet, enabling dynamic updates to the interface based on user selections.
 * 5. Initialization Logic: Sets initial state and behavior for facets, including whether they should be expanded or collapsed based on the presence of selected facet values.
 *
 * Usage:
 * This component is used in conjunction with a product listing page (PLP) to enhance the filtering functionality. It allows users to narrow down product search results based on specific criteria, improving the browsing experience and facilitating product discovery.
 */

const SHOW_BY_DEFAULT = 6;

const handleFacetOnChange = (facet, value, event) => {

  const { target } = event;

  if (facet.type === 'RADIO') {
    const val = target._x_model.get();
    if (Array.isArray(val) && val.includes(value.label)) {
      target._x_model.set([value.label]);
    }
  }
};

const productsBrowserFacet = () => ({
  expanded: false,
  showAll: false,

  get selectedFacetCount() {
    return this.activeFacets[this.facet.id].length;
  },

  get label() {
    return this.facet.label;
  },

  get showByDefault() {

    return SHOW_BY_DEFAULT;
  },

  get hasMoreToggle() {
    return this.facet.values.length > this.showByDefault;
  },

  get values() {
    return this.facet.values;
  },

  init() {
    if (this.selectedFacetCount > 0) {
      this.expanded = this.facet.collapse === 0;
    }
  },

  onChange(facet, value, event) {
    handleFacetOnChange(facet, value, event);
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productsBrowserFacet', productsBrowserFacet);
});
